/**
 * The editor's DOM, back to markdown.
 *
 * What is stored stays markdown — plain, greppable, and the same text whether
 * it was typed as markdown or formatted as you went. This walks only the tags
 * the editor can produce; anything else is read for its text and nothing more,
 * so a paste from a web page cannot smuggle structure into the file.
 */
const BLOCK = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'PRE', 'UL', 'OL', 'LI']);

function inline(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof HTMLElement)) return '';

  const inner = Array.from(node.childNodes).map(inline).join('');
  switch (node.tagName) {
    case 'STRONG':
    case 'B':
      return inner.trim() ? `**${inner}**` : inner;
    case 'EM':
    case 'I':
      return inner.trim() ? `*${inner}*` : inner;
    case 'S':
    case 'STRIKE':
    case 'DEL':
      return inner.trim() ? `~~${inner}~~` : inner;
    case 'CODE':
      return inner.trim() ? `\`${inner}\`` : inner;
    case 'A': {
      const href = node.getAttribute('href');
      return href ? `[${inner}](${href})` : inner;
    }
    case 'BR':
      return '\n';
    default:
      return inner;
  }
}

/**
 * Each push is a whole block — a heading, a paragraph, an entire list — and
 * they are joined by a blank line at the end. Markdown needs that blank line
 * between blocks, so without it what the editor writes would not parse back
 * into what the editor showed.
 */
function block(node: Node, out: string[], depth = 0): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? '').trim();
    if (text) out.push(text);
    return;
  }
  if (!(node instanceof HTMLElement)) return;

  const children = Array.from(node.childNodes);
  switch (node.tagName) {
    case 'H1':
    case 'H2':
    case 'H3':
    case 'H4':
      out.push(`${'#'.repeat(Number(node.tagName[1]))} ${inline(node).trim()}`);
      return;
    case 'BLOCKQUOTE':
      // A quote may run to several lines; each carries its own marker, and the
      // whole thing is one block.
      out.push(
        inline(node)
          .split('\n')
          .map((line) => `> ${line.trim()}`)
          .join('\n'),
      );
      return;
    case 'PRE':
      out.push(['```', node.textContent ?? '', '```'].join('\n'));
      return;
    case 'UL':
    case 'OL': {
      const lines: string[] = [];
      let n = 1;
      for (const child of children) {
        if (!(child instanceof HTMLElement) || child.tagName !== 'LI') continue;
        const nested: string[] = [];
        const own = Array.from(child.childNodes).filter((c) => {
          if (c instanceof HTMLElement && (c.tagName === 'UL' || c.tagName === 'OL')) {
            block(c, nested, depth + 1);
            return false;
          }
          return true;
        });
        const text = own.map(inline).join('').trim();
        const marker = node.tagName === 'OL' ? `${n}.` : '-';
        lines.push(`${'  '.repeat(depth)}${marker} ${text}`);
        lines.push(...nested);
        n += 1;
      }
      // A list is one block, however many items it has.
      if (lines.length) out.push(lines.join('\n'));
      return;
    }
    case 'HR':
      out.push('---');
      return;
    default: {
      // A div or p that only wraps other blocks is a container, not a line.
      const wrapsBlocks = children.some((c) => c instanceof HTMLElement && BLOCK.has(c.tagName));
      if (wrapsBlocks) {
        for (const child of children) block(child, out, depth);
        return;
      }
      // An empty paragraph is just the gap the editor leaves when you press
      // Enter twice; the blank line between blocks is added on the way out.
      const text = inline(node).trim();
      if (text) out.push(text);
    }
  }
}

export function htmlToMarkdown(root: HTMLElement): string {
  const out: string[] = [];
  for (const child of Array.from(root.childNodes)) block(child, out);
  return out
    .filter((piece) => piece.trim())
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
